import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import WifiIcon from "@mui/icons-material/Wifi";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LockIcon from "@mui/icons-material/Lock";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import {
  useGetTemplatesQuery,
  useGetPhoneNUmberQuery,
  useDeleteTemplateMutation,
  useUpdateWhatsappTemplateMutation,
  useCreateTemplateMutation,
} from "../store/services/whatsappApi";
import { cellSX, layoutCSS } from "../component/common/commonStyle";

// ─────────────────────────────────────────────────────────────
// TEMPLATE TYPE DETECTOR
// ─────────────────────────────────────────────────────────────
const getTemplateType = (template) => {
  const components = template?.components || [];
  if (components.some((c) => c.type === "CAROUSEL")) return "Carousel";
  const header = components.find((c) => c.type === "HEADER");
  if (header && (header.format === "IMAGE" || header.format === "VIDEO")) return "Rich Card";
  const hasOTP = components.some((c) => c.type === "BUTTONS" && c.buttons?.some((b) => b.type === "OTP"));
  if (hasOTP) return "Authentication";
  return "Text";
};

// ─────────────────────────────────────────────────────────────
// REJECTION REASON — prefers rejection_details, falls back to
// error_details. Both fields are raw backend dumps that often look
// like:
//   Template creation failed: {"error":{"message":"...","type":"OAuthException","code":192,"fbtrace_id":"..."}}
//   Stack: Error: Template creation failed: ...
//     at WATemplateRequestService.processTemplateRequest (...)
// We strip the trailing "Stack: ..." trace and, where possible, pull
// out just the human-readable Meta `error.message` from the embedded
// JSON so the table shows something readable instead of a wall of text.
// ─────────────────────────────────────────────────────────────
const getRejectionReason = (item) => {
  const raw = item?.rejection_details || item?.error_details || "";
  if (!raw || typeof raw !== "string") return "";

  // Drop the stack trace tail, if present
  const withoutStack = raw.split(/\n\s*Stack:/i)[0].trim();

  // Try to pull the embedded JSON blob and read error.message out of it
  const jsonMatch = withoutStack.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      const metaMessage = parsed?.error?.message;
      if (metaMessage) return metaMessage;
    } catch {
      // not valid JSON — fall through and use the raw text below
    }
  }

  return withoutStack || raw;
};

const REJECTION_REASON_TRUNCATE_AT = 25;

// ─────────────────────────────────────────────────────────────
// HEADER MEDIA — handles IMAGE, VIDEO, and fallback
// Accepts:
//   url    – direct CDN URL string (or null/undefined)
//   format – "IMAGE" | "VIDEO" (default "IMAGE")
//   height – number (px)
// ─────────────────────────────────────────────────────────────
const HeaderMedia = ({ url, format = "IMAGE", height = 130 }) => {
  const [imgStatus, setImgStatus] = useState("loading");

  useEffect(() => {
    if (format === "IMAGE") setImgStatus("loading");
  }, [url, format]);

  // ── No URL ──────────────────────────────────────────────────
  if (!url) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          bgcolor: "#efefef",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        {format === "VIDEO" ? (
          <VideoLibraryIcon sx={{ color: "#bbb", fontSize: 30 }} />
        ) : (
          <ImageIcon sx={{ color: "#bbb", fontSize: 30 }} />
        )}
        <Typography sx={{ fontSize: "0.6rem", color: "#bbb" }}>
          No {format === "VIDEO" ? "video" : "image"}
        </Typography>
      </Box>
    );
  }

  // ── VIDEO ───────────────────────────────────────────────────
  if (format === "VIDEO") {
    return (
      <Box sx={{ position: "relative", width: "100%", height, bgcolor: "#000" }}>
        <Box
          component="video"
          src={url}
          controls
          preload="metadata"
          sx={{
            width: "100%",
            height,
            objectFit: "contain",
            display: "block",
          }}
        >
          {/* Fallback for browsers that can't play inline */}
          <Box
            sx={{
              width: "100%",
              height,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#1a1a1a",
            }}
          >
            <VideoLibraryIcon sx={{ color: "#aaa", fontSize: 32 }} />
            <Typography sx={{ fontSize: "0.6rem", color: "#aaa", mt: 0.5 }}>
              Video unavailable
            </Typography>
          </Box>
        </Box>

        {/* Small "VIDEO" pill badge top-left */}
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            px: 0.7,
            py: 0.2,
            bgcolor: "rgba(0,0,0,0.55)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: 0.4,
            pointerEvents: "none",
          }}
        >
          <VideoLibraryIcon sx={{ fontSize: 11, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.55rem", color: "#fff", fontWeight: 700 }}>
            VIDEO
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── IMAGE (with shimmer + error fallback) ───────────────────
  return (
    <Box sx={{ position: "relative", width: "100%", height }}>
      {imgStatus !== "loaded" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s infinite",
            "@keyframes shimmer": {
              "0%": { backgroundPosition: "200% 0" },
              "100%": { backgroundPosition: "-200% 0" },
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {imgStatus === "error" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <ImageIcon sx={{ color: "#ccc", fontSize: 26 }} />
              <Typography sx={{ fontSize: "0.58rem", color: "#ccc", mt: 0.3 }}>
                Image unavailable
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box
        component="img"
        src={url}
        alt="header"
        onLoad={() => setImgStatus("loaded")}
        onError={() => setImgStatus("error")}
        sx={{
          width: "100%",
          height,
          objectFit: "cover",
          display: "block",
          opacity: imgStatus === "loaded" ? 1 : 0,
          transition: "opacity 0.3s",
        }}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  APPROVED: { bg: "#d4edda", color: "#155724" },
  PENDING: { bg: "#fff3cd", color: "#856404" },
  REJECTED: { bg: "#f8d7da", color: "#721c24" },
  PAUSED: { bg: "#f8d7da", color: "#721c24" },
};
const statusStyle = (s) =>
  STATUS_STYLES[String(s || "").toUpperCase()] ?? { bg: "#e2e3e5", color: "#383d41" };

// ─────────────────────────────────────────────────────────────
// QUALITY / HEALTH HELPERS
// Meta quality ratings: GREEN (High), YELLOW (Medium), RED (Low), UNKNOWN
// ─────────────────────────────────────────────────────────────
const NEUTRAL_BADGE = { bg: "#e2e3e5", color: "#383d41" };
const GOOD_BADGE = { bg: "#d4edda", color: "#155724" };
const WARN_BADGE = { bg: "#fff3cd", color: "#856404" };
const BAD_BADGE = { bg: "#f8d7da", color: "#721c24" };

const HEALTH_MAP = {
  HEALTHY: GOOD_BADGE,
  GOOD: GOOD_BADGE,
  ACTIVE: GOOD_BADGE,
  WARNING: WARN_BADGE,
  AT_RISK: WARN_BADGE,
  DEGRADED: WARN_BADGE,
  CRITICAL: BAD_BADGE,
  POOR: BAD_BADGE,
  UNHEALTHY: BAD_BADGE,
  PAUSED: BAD_BADGE,
  DISABLED: BAD_BADGE,
};

const getHealthBadge = (status) => {
  if (!status) return { label: "No data", ...NEUTRAL_BADGE };
  const key = String(status).toUpperCase().replace(/\s+/g, "_");
  return { label: String(status).replace(/_/g, " "), ...(HEALTH_MAP[key] ?? NEUTRAL_BADGE) };
};

const Badge = ({ label, bg, color, dot = false }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.6,
      px: 1, py: "2px", borderRadius: "12px",
      fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap",
      bgcolor: bg, color,
    }}
  >
    {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color }} />}
    {label}
  </Box>
);

// ─────────────────────────────────────────────────────────────
// OTP BUTTON DISPLAY HELPER
// ─────────────────────────────────────────────────────────────
const getOTPButtonLabel = (otpType) => {
  const labels = {
    COPY_CODE: "Copy Code",
    ONE_TAP: "One-tap verification",
    ZERO_TAP: "Auto-fill OTP",
  };
  return labels[otpType] || otpType || "Verify Code";
};

// ─────────────────────────────────────────────────────────────
// BUTTON TOOLTIP HELPERS
// Show the phone number for PHONE_NUMBER buttons and the URL for
// URL buttons (WhatsApp URL / Website / Static / Dynamic) in a
// tooltip on hover. Everything else (Quick Reply, OTP, etc.) has
// no tooltip.
// ─────────────────────────────────────────────────────────────
const getButtonTooltip = (btn) => {
  if (!btn) return "";
  if (btn.type === "PHONE_NUMBER" && btn.phone_number) return btn.phone_number;
  if (btn.type === "URL" && btn.url) return btn.url;
  return "";
};

const shouldShowButtonTooltip = (btn) =>
  Boolean(getButtonTooltip(btn));

const HEAD_SX = {
  "& th": { bgcolor: "#0288d1", color: "#fff", fontSize: "0.8rem", p: "6px", borderRight: "0.5px solid #1b1b1b" },
};
const ROW_SX = {
  "& td": { p: "8px", fontSize: "0.75rem", borderRight: "0.5px solid #1b1b1b", borderBottom: "0.5px solid #1b1b1b" },
};

// Column width hints — kept in one place so table header and rows
// stay in sync. Only hints: browser still auto-sizes to content,
// but this biases space so "Last Updated" always fits date + time.
const COLUMNS = [
  { label: "Template Name",    width: "12%" },
  { label: "Category",         width: "7%"  },
  { label: "Phone Number",     width: "8%"  },
  { label: "Type",             width: "7%"  },
  { label: "Status",           width: "8%"  },
  { label: "Language",         width: "7%"  },
  { label: "Health Status",    width: "11%" },
  { label: "Rejection Reason", width: "9%"  },
  { label: "Last Updated",     width: "10%" },
  { label: "Actions",          width: "7%"  },
];

// ─────────────────────────────────────────────────────────────
// EDIT / COPY TEMPLATE DIALOG
// ─────────────────────────────────────────────────────────────
const BRAND = "#0288d1";
const EDIT_BORDER = "#e2e8f0";
const BODY_MAX = 1024;
const BUTTON_TEXT_MAX = 25;

const editFieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
  "& .MuiFormHelperText-root": { fontSize: "0.68rem", mx: 0, mt: 0.5 },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": { borderColor: "#CBD5E1" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
    "&.Mui-focused fieldset": { borderColor: BRAND, borderWidth: "1.5px" },
  },
};

// Same field look, light grey fill — used for read-only values
const lockedFieldSx = {
  ...editFieldSx,
  "& .MuiOutlinedInput-root": {
    ...editFieldSx["& .MuiOutlinedInput-root"],
    bgcolor: "#f8fafc",
  },
};

const EditSectionLabel = ({ children, right }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
    <Typography
      sx={{
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "#64748b",
      }}
    >
      {children}
    </Typography>
    {right}
  </Box>
);

// Small pill used across the dialog
const Chip = ({ text, sx }) => (
  <Box
    component="span"
    sx={{
      px: 1,
      py: "2px",
      borderRadius: "12px",
      fontSize: "0.66rem",
      fontWeight: 600,
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {text}
  </Box>
);

const EditTemplateDialog = ({
  open,
  onClose,
  template,
  formData,
  setFormData,
  onSubmit,
  isSubmitting = false,
  isCopy = false,
  fullyEdit = false,
  onFullyEditChange,
}) => {
  const components = Array.isArray(template?.components) ? template.components : [];
  const hasTextHeader = components.some((c) => c.type === "HEADER" && c.format === "TEXT");
  const hasFooter = components.some((c) => c.type === "FOOTER");
  const isApproved = !isCopy && String(template?.status || "").toUpperCase() === "APPROVED";
  const mediaHeader = components.find(
    (c) => c.type === "HEADER" && (c.format === "IMAGE" || c.format === "VIDEO")
  );
  const carouselCards = components.find((c) => c.type === "CAROUSEL")?.cards || [];
  const { bg, color } = statusStyle(template?.status);

  const variables = [...new Set(formData.bodyText?.match(/\{\{\d+\}\}/g) || [])];
  const bodyLen = formData.bodyText?.length || 0;
  const bodyTooLong = bodyLen > BODY_MAX;
  const showContent = !isCopy || fullyEdit;

  // Block save if any button is missing its text / URL / phone number
  const buttonsInvalid = (formData.buttons || []).some((b) => {
    if (b.type === "OTP") return false;
    if (!b.text?.trim()) return true;
    if (b.type === "URL" && !b.url?.trim()) return true;
    if (b.type === "PHONE_NUMBER" && !b.phone_number?.trim()) return true;
    return false;
  });

  // Meta rule for template names: lowercase letters, numbers, underscores
  const nameInvalid = isCopy && !!formData.name?.trim() && !/^[a-z0-9_]+$/.test(formData.name.trim());

  const categories = [
    ...new Set(["MARKETING", "UTILITY", "AUTHENTICATION", formData.category].filter(Boolean)),
  ];
  const categoryLocked = isApproved || (isCopy && !fullyEdit);

  const copySummary = [
    hasTextHeader && "Header text",
    mediaHeader && (mediaHeader.format === "VIDEO" ? "Video header" : "Image header"),
    hasFooter && "Footer",
    carouselCards.length > 0 && `${carouselCards.length} carousel cards`,
    (formData.buttons || []).length > 0 &&
      `${formData.buttons.length} button${formData.buttons.length > 1 ? "s" : ""}`,
  ].filter(Boolean);

  const set = (patch) => setFormData({ ...formData, ...patch });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: isCopy ? "linear-gradient(135deg,#0288d1 0%,#26a69a 100%)" : BRAND,
          color: "#fff",
          px: 2.5,
          py: 1.5,
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isCopy ? <ContentCopyIcon sx={{ fontSize: 18 }} /> : <EditOutlinedIcon sx={{ fontSize: 18 }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1.2 }}>
            {isCopy ? "Copy Template" : "Edit Template"}
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.7rem", opacity: 0.9 }}>
            {isCopy ? "Create a new template from an existing one" : template?.name}
          </Typography>
        </Box>
        {!isCopy && template?.status && (
          <Chip text={template.status} sx={{ bgcolor: bg, color, fontWeight: 700 }} />
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff", mr: -0.75, flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: "#fff" }}>
        <Stack spacing={2.5}>
          {/* ── Source card (copy only) ── */}
          {isCopy && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                bgcolor: "#f0f9ff",
                border: "1px solid #bae6fd",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.64rem",
                    color: "#64748b",
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  Copying from
                </Typography>
                <Typography noWrap sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" }}>
                  {template?.name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.6, flexShrink: 0 }}>
                {template?.status && (
                  <Chip text={template.status} sx={{ bgcolor: bg, color, fontWeight: 700 }} />
                )}
                {template?.language && (
                  <Chip
                    text={template.language}
                    sx={{ bgcolor: "#fff", color: "#0277bd", border: "1px solid #bae6fd" }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* ── Basics: Name | Category (| Language) — one aligned row ── */}
          <Box>
            <EditSectionLabel>{isCopy ? "New template" : "Basics"}</EditSectionLabel>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isCopy && fullyEdit ? "1.5fr 1fr 0.8fr" : "1.4fr 1fr",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                label="Template Name"
                size="small"
                fullWidth
                required={isCopy}
                value={isCopy ? formData.name || "" : template?.name || ""}
                onChange={(e) => set({ name: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                error={nameInvalid}
                InputProps={{ readOnly: !isCopy }}
                sx={isCopy ? editFieldSx : lockedFieldSx}
              />

              <TextField
                select
                label="Category"
                size="small"
                fullWidth
                value={formData.category || ""}
                onChange={(e) => set({ category: e.target.value })}
                InputProps={{ readOnly: categoryLocked }}
                sx={categoryLocked ? lockedFieldSx : editFieldSx}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c} sx={{ fontSize: "0.8rem" }}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>

              {isCopy && fullyEdit && (
                <TextField
                  label="Language"
                  size="small"
                  fullWidth
                  value={formData.language || ""}
                  onChange={(e) => set({ language: e.target.value })}
                  sx={editFieldSx}
                />
              )}
            </Box>
            {isCopy && (
              <Typography sx={{ fontSize: "0.68rem", mt: 0.7, color: nameInvalid ? "#b91c1c" : "#94a3b8" }}>
                {nameInvalid
                  ? "Only lowercase letters, numbers and underscores are allowed"
                  : "Name: lowercase letters, numbers and underscores only"}
              </Typography>
            )}
          </Box>

          {/* ── Fully edit toggle card (copy only) ── */}
          {isCopy && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                p: 1.5,
                borderRadius: "10px",
                transition: "all .2s",
                border: `1px solid ${fullyEdit ? BRAND : EDIT_BORDER}`,
                bgcolor: fullyEdit ? "#f0f9ff" : "#fff",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f172a" }}>
                  Fully edit
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                  {fullyEdit
                    ? "Change category, language, body, header, footer and buttons before creating."
                    : "Off — an exact copy of the original will be created."}
                </Typography>
              </Box>
              <Switch
                checked={fullyEdit}
                onChange={(e) => onFullyEditChange(e.target.checked)}
                color="primary"
                sx={{ mr: -1, flexShrink: 0 }}
              />
            </Box>
          )}

          {/* ── Read-only summary (copy, not fully edit) ── */}
          {isCopy && !fullyEdit && (
            <Box>
              <EditSectionLabel>What will be copied</EditSectionLabel>
              <Box sx={{ p: 1.5, borderRadius: "10px", border: `1px solid ${EDIT_BORDER}`, bgcolor: "#f8fafc" }}>
                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    color: "#334155",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {formData.bodyText || "No body text"}
                </Typography>
                {copySummary.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 1.2 }}>
                    {copySummary.map((s) => (
                      <Chip
                        key={s}
                        text={s}
                        sx={{ bgcolor: "#e2e8f0", color: "#334155", borderRadius: "6px", fontSize: "0.64rem" }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ── Content ── */}
          {showContent && (
            <Box>
              <EditSectionLabel>Content</EditSectionLabel>
              <Stack spacing={1.5}>
                {/* Rich card media (image / video) — read-only preview */}
                {mediaHeader && (
                  <Box>
                    <Typography sx={{ fontSize: "0.68rem", color: "#64748b", mb: 0.4 }}>
                      Header {mediaHeader.format === "VIDEO" ? "Video" : "Image"}
                    </Typography>
                    <Box sx={{ borderRadius: "8px", overflow: "hidden", border: `1px solid ${EDIT_BORDER}` }}>
                      <HeaderMedia
                        url={mediaHeader.example?.header_handle?.[0] ?? null}
                        format={mediaHeader.format}
                        height={mediaHeader.format === "VIDEO" ? 170 : 150}
                      />
                    </Box>
                    <Typography sx={{ fontSize: "0.66rem", color: "#94a3b8", mt: 0.5 }}>
                      Media is kept as-is and can't be changed here.
                    </Typography>
                  </Box>
                )}

                {/* Carousel cards — read-only preview strip */}
                {carouselCards.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: "0.68rem", color: "#64748b", mb: 0.4 }}>
                      Carousel Cards ({carouselCards.length})
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.2,
                        overflowX: "auto",
                        pb: 0.8,
                        "&::-webkit-scrollbar": { height: "4px" },
                        "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: "2px" },
                      }}
                    >
                      {carouselCards.map((card, idx) => {
                        const cHeader = card.components?.find((c) => c.type === "HEADER");
                        const cBody = card.components?.find((c) => c.type === "BODY");
                        const cBtns = card.components?.find((c) => c.type === "BUTTONS");
                        const fmt = cHeader?.format === "VIDEO" ? "VIDEO" : "IMAGE";
                        return (
                          <Box
                            key={idx}
                            sx={{
                              width: 150,
                              flexShrink: 0,
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: `1px solid ${EDIT_BORDER}`,
                              bgcolor: "#fff",
                            }}
                          >
                            {(cHeader?.format === "IMAGE" || cHeader?.format === "VIDEO") && (
                              <HeaderMedia
                                url={cHeader?.example?.header_handle?.[0] ?? null}
                                format={fmt}
                                height={fmt === "VIDEO" ? 110 : 90}
                              />
                            )}
                            <Box sx={{ p: 0.8 }}>
                              <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#64748b" }}>
                                Card {idx + 1}
                              </Typography>
                              {cBody?.text && (
                                <Typography
                                  sx={{
                                    fontSize: "0.68rem",
                                    color: "#334155",
                                    lineHeight: 1.35,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {cBody.text}
                                </Typography>
                              )}
                              {cBtns?.buttons?.length > 0 && (
                                <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap", mt: 0.6 }}>
                                  {cBtns.buttons.map((b, bi) => (
                                    <Chip
                                      key={bi}
                                      text={b.text}
                                      sx={{ bgcolor: "#f1f5f9", color: "#334155", borderRadius: "6px", fontSize: "0.6rem" }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    <Typography sx={{ fontSize: "0.66rem", color: "#94a3b8", mt: 0.3 }}>
                      Card media and buttons are kept as-is. Only the main body text below is edited.
                    </Typography>
                  </Box>
                )}

                {hasTextHeader && (
                  <TextField
                    label="Header Text"
                    size="small"
                    fullWidth
                    value={formData.headerText}
                    onChange={(e) => set({ headerText: e.target.value })}
                    sx={editFieldSx}
                  />
                )}

                <Box>
                  <TextField
                    label="Body Text"
                    size="small"
                    fullWidth
                    multiline
                    rows={5}
                    value={formData.bodyText}
                    onChange={(e) => set({ bodyText: e.target.value })}
                    error={bodyTooLong}
                    sx={editFieldSx}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 0.5,
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.68rem", color: "#64748b" }}>
                        {variables.length ? "Variables:" : "Use {{1}}, {{2}} for variables"}
                      </Typography>
                      {variables.map((v) => (
                        <Chip
                          key={v}
                          text={v}
                          sx={{ bgcolor: "#e0f2fe", color: "#0277bd", borderRadius: "6px", fontSize: "0.66rem" }}
                        />
                      ))}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: bodyTooLong ? "#b91c1c" : "#94a3b8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {bodyLen} / {BODY_MAX}
                    </Typography>
                  </Box>
                </Box>

                {hasFooter && (
                  <TextField
                    label="Footer Text"
                    size="small"
                    fullWidth
                    value={formData.footerText}
                    onChange={(e) => set({ footerText: e.target.value })}
                    sx={editFieldSx}
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* ── Buttons — text, URL and phone number are all editable ── */}
          {showContent && formData.buttons.length > 0 && (
            <Box>
              <EditSectionLabel
                right={
                  <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8" }}>
                    {formData.buttons.length} button{formData.buttons.length > 1 ? "s" : ""}
                  </Typography>
                }
              >
                Buttons
              </EditSectionLabel>
              <Stack spacing={1}>
                {formData.buttons.map((btn, i) => {
                  const isOtp = btn.type === "OTP";
                  const isUrl = btn.type === "URL";
                  const isPhone = btn.type === "PHONE_NUMBER";

                  const updateBtn = (patch) => {
                    const nb = [...formData.buttons];
                    nb[i] = { ...nb[i], ...patch };
                    set({ buttons: nb });
                  };

                  const handleUrlChange = (e) => {
                    const url = e.target.value;
                    // Meta requires an example when the URL ends with a {{1}} variable
                    const hasVar = /\{\{\d+\}\}$/.test(url);
                    updateBtn({
                      url,
                      example: hasVar ? [url.replace(/\{\{\d+\}\}$/, "sample")] : undefined,
                    });
                  };

                  const inputSx = {
                    ...editFieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...editFieldSx["& .MuiOutlinedInput-root"],
                      bgcolor: "#fff",
                    },
                  };

                  return (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.2,
                        p: 1.2,
                        border: `1px solid ${EDIT_BORDER}`,
                        borderRadius: "10px",
                        bgcolor: "#f8fafc",
                      }}
                    >
                      <Chip
                        text={String(btn.type || "BUTTON").replace(/_/g, " ")}
                        sx={{
                          mt: 0.8,
                          bgcolor: "#e2e3e5",
                          color: "#383d41",
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          minWidth: 62,
                          textAlign: "center",
                        }}
                      />

                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        {/* Button text */}
                        <TextField
                          size="small"
                          fullWidth
                          label="Button Text"
                          placeholder={`Button ${i + 1} text`}
                          value={btn.text ?? ""}
                          disabled={isOtp}
                          inputProps={{ maxLength: BUTTON_TEXT_MAX }}
                          error={!isOtp && !btn.text?.trim()}
                          helperText={!isOtp ? `${(btn.text || "").length} / ${BUTTON_TEXT_MAX}` : undefined}
                          onChange={(e) => updateBtn({ text: e.target.value })}
                          sx={inputSx}
                        />

                        {/* URL value */}
                        {isUrl && (
                          <TextField
                            size="small"
                            fullWidth
                            label="Website URL"
                            placeholder="https://example.com/page or https://example.com/{{1}}"
                            value={btn.url ?? ""}
                            error={!btn.url?.trim()}
                            helperText="Use {{1}} at the end of the URL for a dynamic link"
                            onChange={handleUrlChange}
                            sx={inputSx}
                          />
                        )}

                        {/* Phone value */}
                        {isPhone && (
                          <TextField
                            size="small"
                            fullWidth
                            label="Phone Number"
                            placeholder="+919876543210"
                            value={btn.phone_number ?? ""}
                            error={!btn.phone_number?.trim()}
                            helperText="Include country code, e.g. +91..."
                            onChange={(e) =>
                              updateBtn({ phone_number: e.target.value.replace(/[^\d+]/g, "") })
                            }
                            sx={inputSx}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1,
          justifyContent: "flex-end",
          borderTop: `1px solid ${EDIT_BORDER}`,
          bgcolor: "#fafbfc",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ textTransform: "none", fontSize: "0.78rem", borderRadius: "8px", px: 2, height: 34, minWidth: 90 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onSubmit}
          disabled={
            isSubmitting ||
            (isCopy
              ? !formData.name?.trim() ||
                nameInvalid ||
                (fullyEdit && (bodyTooLong || !formData.bodyText?.trim() || buttonsInvalid))
              : bodyTooLong || !formData.bodyText?.trim() || buttonsInvalid)
          }
          startIcon={!isSubmitting && isCopy ? <ContentCopyIcon sx={{ fontSize: 16 }} /> : null}
          sx={{ textTransform: "none", fontSize: "0.78rem", borderRadius: "8px", px: 2.5, height: 34, minWidth: 140 }}
        >
          {isSubmitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : isCopy ? (
            "Create Template"
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getTemplateFormData = (template) => {
  const components = Array.isArray(template?.components) ? template.components : [];
  return {
    name: template?.name || "",
    bodyText: components.find((component) => component.type === "BODY")?.text || "",
    footerText: components.find((component) => component.type === "FOOTER")?.text || "",
    headerText:
      components.find((component) => component.type === "HEADER" && component.format === "TEXT")?.text || "",
    buttons: (components.find((component) => component.type === "BUTTONS")?.buttons || []).map(
      (button) => ({ ...button })
    ),
    category: template?.category || "",
    language: template?.language || "en_US",
  };
};

const buildTemplateComponents = (template, formData) =>
  (Array.isArray(template?.components) ? template.components : []).map((component) => {
    if (component.type === "BODY") {
      const matches = formData.bodyText.match(/\{\{\d+\}\}/g) || [];
      const example = { ...(component.example || {}) };
      if (matches.length) {
        const currentExamples = component.example?.body_text?.[0] || [];
        example.body_text = [matches.map((_, index) => currentExamples[index] || "sample")];
      } else {
        delete example.body_text;
      }
      return {
        ...component,
        text: formData.bodyText,
        ...(Object.keys(example).length ? { example } : { example: undefined }),
      };
    }
    if (component.type === "FOOTER" && typeof component.text === "string") {
      return { ...component, text: formData.footerText };
    }
    if (component.type === "HEADER" && component.format === "TEXT") {
      return { ...component, text: formData.headerText };
    }
    if (component.type === "BUTTONS") {
      return {
        ...component,
        buttons: formData.buttons.map((button) => ({ ...button })),
      };
    }
    return component;
  });

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
function WhatsappList() {
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [lastUpdateFilter, setLastUpdateFilter] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editFormData, setEditFormData] = useState({
    bodyText: "",
    footerText: "",
    headerText: "",
    buttons: [],
    category: "",
    language: "",
  });
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyingTemplate, setCopyingTemplate] = useState(null);
  const [copyFormData, setCopyFormData] = useState({
    name: "",
    bodyText: "",
    footerText: "",
    headerText: "",
    buttons: [],
    category: "",
    language: "",
  });
  const [copyFullyEdit, setCopyFullyEdit] = useState(false);
  const [updateWhatsappTemplate, { isLoading: isEditing }] = useUpdateWhatsappTemplateMutation();
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation();

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) { toast.error("Access token not found"); return; }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch { toast.error("Invalid access token format"); }
  }, []);

  const {
    data: templateData = { templates: [] },
    isLoading,
    isError,
    refetch,
  } = useGetTemplatesQuery({ customer_id: customerId }, { skip: !customerId });
  const { data: phoneData } = useGetPhoneNUmberQuery(customerId, { skip: !customerId });

  const [deleteTemplate] = useDeleteTemplateMutation();

  const uniqueCategories = useMemo(
    () => [...new Set((templateData?.templates ?? []).map((t) => t.category).filter(Boolean))],
    [templateData]
  );

  const uniqueStatuses = useMemo(
    () => [...new Set((templateData?.templates ?? []).map((t) => t.status).filter(Boolean))],
    [templateData]
  );

  const filteredTemplates = useMemo(() => {
    return (templateData?.templates ?? []).filter((t) => {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        !s ||
        t.name?.toLowerCase().includes(s) ||
        t.template_id?.toLowerCase().includes(s) ||
        t.waba_id?.toLowerCase().includes(s);
      const matchCat = !categoryFilter || t.category === categoryFilter;
      const matchStatus = !statusFilter || t.status === statusFilter;
      let matchDate = true;
      if (lastUpdateFilter && t.created_at) {
        const days = Math.floor((Date.now() - new Date(t.created_at)) / 86400000);
        matchDate = lastUpdateFilter === "recent" ? days <= 7 : days > 30;
      }
      return matchSearch && matchCat && matchStatus && matchDate;
    });
  }, [templateData, searchTerm, categoryFilter, statusFilter, lastUpdateFilter]);

  const sortedTemplates = useMemo(
    () =>
      [...filteredTemplates].sort((a, b) => {
        const dA = new Date(a.created_at || 0).getTime();
        const dB = new Date(b.created_at || 0).getTime();
        return lastUpdateFilter === "oldest" ? dA - dB : dB - dA;
      }),
    [filteredTemplates, lastUpdateFilter]
  );

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page items
  const paginatedTemplates = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedTemplates.slice(startIndex, endIndex);
  }, [sortedTemplates, page, rowsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, categoryFilter, statusFilter, lastUpdateFilter]);

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await deleteTemplate({ customer_id: customerId, name }).unwrap();
      toast.success("Template deleted successfully");
      refetch();
    } catch {
      toast.error("Failed to delete template");
    }
  };

  const handleOpenEdit = (template) => {
    setEditingTemplate(template);
    setEditFormData(getTemplateFormData(template));
    setEditDialogOpen(true);
  };

  const handleOpenCopy = (template) => {
    setCopyingTemplate(template);
    setCopyFormData({
      ...getTemplateFormData(template),
      name: template?.name ? `${template.name}_copy` : "",
    });
    setCopyFullyEdit(false);
    setCopyDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      if (!editingTemplate || !customerId) return;
      const components = buildTemplateComponents(editingTemplate, editFormData);

      await updateWhatsappTemplate({
        customer_id: customerId,
        template_id: editingTemplate.template_id,
        body: {
          category: editFormData.category,
          components,
          language: editFormData.language,
        },
      }).unwrap();
      toast.success("Template updated");
      setEditDialogOpen(false);
      refetch();
    } catch (e) {
      toast.error(e?.data?.error?.message || "Failed to update template");
    }
  };

  const handleCopySubmit = async () => {
    if (!copyingTemplate || !customerId) return;
    try {
      const components = copyFullyEdit
        ? buildTemplateComponents(copyingTemplate, copyFormData)
        : Array.isArray(copyingTemplate.components)
          ? copyingTemplate.components
          : [];
      const hasOtpButton = components.some(
        (component) => component.type === "BUTTONS" && component.buttons?.some((button) => button.type === "OTP")
      );
      const hasCarousel = components.some((component) => component.type === "CAROUSEL");
      const hasMediaHeader = components.some(
        (component) =>
          component.type === "HEADER" &&
          ["IMAGE", "VIDEO", "DOCUMENT"].includes(String(component.format).toUpperCase())
      );
      const sourcePhone = phoneData?.phone_numbers?.find(
        (phone) =>
          phone.phone_number_id === copyingTemplate.phone_number_id ||
          phone.phone_number === copyingTemplate.phone_number
      );
      const formData = new FormData();
      formData.append("customer_id", String(customerId));
      formData.append("name", copyFormData.name.trim());
      formData.append("language", copyFormData.language || "en_US");
      formData.append("category", (copyFormData.category || "MARKETING").toUpperCase());
      formData.append(
        "template_type",
        hasOtpButton ? "TEXT" : hasCarousel ? "INTERACTIVE" : hasMediaHeader ? "MEDIA" : "TEXT"
      );
      formData.append("allow_category_change", "true");
      formData.append("phone_number_id", copyingTemplate.phone_number_id || sourcePhone?.phone_number_id || "");
      formData.append("waba_id", copyingTemplate.waba_id || sourcePhone?.waba_id || "");
      formData.append("components", JSON.stringify(components));

      await createTemplate(formData).unwrap();
      toast.success("Template created successfully");
      setCopyDialogOpen(false);
      setCopyingTemplate(null);
      refetch();
    } catch (e) {
      toast.error(e?.data?.error?.message || "Failed to create template");
    }
  };

  // ── Carousel sub-component (defined inside render so it closes over cards) ──
  const CarouselPreviewGray = ({ cards }) => {
    const [activeIdx, setActiveIdx] = useState(0);
    const safeCards = Array.isArray(cards) ? cards : [];

    if (!safeCards.length) {
      return (
        <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: "0.7rem", color: "#aaa" }}>No carousel cards</Typography>
        </Box>
      );
    }

    const currentCard = safeCards[activeIdx];
    const cardHeader = currentCard?.components?.find((c) => c.type === "HEADER");
    const cardBody = currentCard?.components?.find((c) => c.type === "BODY");
    const cardBtns = currentCard?.components?.find((c) => c.type === "BUTTONS");

    // Resolve media URL + format for this card
    const cardMediaUrl = cardHeader?.example?.header_handle?.[0] ?? null;
    const cardMediaFormat = cardHeader?.format ?? "IMAGE"; // "IMAGE" | "VIDEO"

    return (
      <Box sx={{ mb: 1 }}>
        {/* Chip navigation ON TOP */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
          <IconButton size="small" disabled={activeIdx === 0} onClick={() => setActiveIdx((p) => p - 1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          {safeCards.map((_, i) => (
            <Box
              key={i}
              onClick={() => setActiveIdx(i)}
              sx={{
                width: i === activeIdx ? 14 : 6,
                height: 6,
                borderRadius: "3px",
                bgcolor: i === activeIdx ? "#1976d2" : "#ccc",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            />
          ))}
          <IconButton
            size="small"
            disabled={activeIdx === safeCards.length - 1}
            onClick={() => setActiveIdx((p) => p + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Active card */}
        <Box sx={{ bgcolor: "#fff", borderRadius: "8px", overflow: "hidden" }}>
          {/* Media: IMAGE or VIDEO */}
          {(cardHeader?.format === "IMAGE" || cardHeader?.format === "VIDEO") && (
            <HeaderMedia
              url={cardMediaUrl}
              format={cardMediaFormat}
              height={cardMediaFormat === "VIDEO" ? 140 : 110}
            />
          )}

          {/* Text header */}
          {cardHeader?.format === "TEXT" && cardHeader.text && (
            <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#000", p: 1 }}>
              {cardHeader.text}
            </Typography>
          )}

          {cardBody?.text && (
            <Typography sx={{ p: 1, fontSize: "0.75rem", color: "#333" }}>
              {cardBody.text}
            </Typography>
          )}

          {cardBtns?.buttons?.length > 0 && (
            <Box sx={{ px: 1, pb: 1 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {cardBtns.buttons.map((btn, bIdx) => (
                  <Tooltip
                    key={bIdx}
                    title={getButtonTooltip(btn)}
                    arrow
                    disableHoverListener={!shouldShowButtonTooltip(btn)}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        textTransform: "none",
                        borderRadius: "6px",
                        ...(bIdx === 0
                          ? { bgcolor: "#000", color: "#fff" }
                          : { bgcolor: "#f0f0f0", color: "#333" }),
                      }}
                    >
                      {btn.text}
                    </Button>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {/* Card counter */}
        <Typography sx={{ textAlign: "center", fontSize: "0.6rem", color: "#aaa", mt: 0.3 }}>
          {activeIdx + 1} / {safeCards.length}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={layoutCSS}>
      {/* ── Top bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, mb: 2, p: 1 }}>
        <TextField
          placeholder="Search by name, ID, or business ID"
          size="small"
          sx={{ width: 250, "& .MuiInputBase-input": { fontSize: "0.75rem", py: "6px", px: "8px" } }}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: "#1e88e5", mr: 0.5, fontSize: 20 }} /> }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button
          variant="contained"
          size="small"
          sx={{ textTransform: "none", fontSize: 13, borderRadius: 1.5, px: 2 }}
          onClick={() => navigate("/whatsapp-template")}
        >
          Create Template
        </Button>
      </Box>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center",
        p: "1rem", borderRadius: "0.5rem",
        bgcolor: "#ffffff52", backdropFilter: "blur(10px)",
        boxShadow: "0 4px 12px rgba(14,165,233,0.1)", mb: 2,
      }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Filter by:</Typography>
        {[
          { label: "Category", val: categoryFilter, set: setCategoryFilter, opts: uniqueCategories },
          { label: "Status", val: statusFilter, set: setStatusFilter, opts: uniqueStatuses },
        ].map(({ label, val, set, opts }) => (
          <FormControl key={label} size="small" sx={{ width: 130 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>{label}</InputLabel>
            <Select label={label} value={val} onChange={(e) => set(e.target.value)} sx={{ fontSize: "0.75rem" }}>
              <MenuItem value="">All {label}s</MenuItem>
              {opts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        ))}
        <FormControl size="small" sx={{ width: 145 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Last Update</InputLabel>
          <Select
            label="Last Update"
            value={lastUpdateFilter}
            onChange={(e) => setLastUpdateFilter(e.target.value)}
            sx={{ fontSize: "0.75rem" }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="recent">Last 7 Days</MenuItem>
            <MenuItem value="oldest">Older than 30 Days</MenuItem>
          </Select>
        </FormControl>
        {(searchTerm || categoryFilter || statusFilter || lastUpdateFilter) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchTerm("");
              setCategoryFilter("");
              setStatusFilter("");
              setLastUpdateFilter("");
            }}
          >
            Clear Filters
          </Button>
        )}
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          Showing {paginatedTemplates.length} of {filteredTemplates.length} templates
          {filteredTemplates.length !== (templateData?.templates?.length ?? 0) &&
            ` (filtered from ${templateData?.templates?.length ?? 0} total)`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          p: 1.5,
          mb: 2,
          borderRadius: "10px",
          bgcolor: "#f0f9ff",
          border: "1px solid #bae6fd",
          boxShadow: "inset 0 0 0 1px rgba(14,165,233,0.04)",
        }}
      >
        <Box sx={{ ml: 0.25, flex: 1 }}>
          {[
            "Only templates with APPROVED, REJECTED, or PAUSED status can be edited",
            "Cannot edit individual components - all components will be replaced",
            "Cannot edit category of an approved template",
            "Approved templates: Limited to 10 edits per 30 days OR 1 edit per 24 hours",
            "Rejected/paused templates: Unlimited edits",
          ].map((line) => (
            <Typography
              key={line}
              sx={{ fontSize: "0.72rem", color: "#0f4c75", lineHeight: 1.6, fontWeight: 500, display: "flex", alignItems: "flex-start", gap: 1 }}
            >
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BRAND, mt: "0.52rem", flexShrink: 0 }} />
              <Box component="span">{line}</Box>
            </Typography>
          ))}
        </Box>
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper}>
        <Table stickyHeader sx={{ tableLayout: "auto" }}>
          <TableHead sx={HEAD_SX}>
            <TableRow>
              {COLUMNS.map(({ label, width }) => (
                <TableCell key={label} sx={{ width, whiteSpace: "nowrap" }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center">Loading templates...</TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center">Error loading templates</TableCell>
              </TableRow>
            ) : paginatedTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center">
                  {(templateData?.templates?.length ?? 0) > 0
                    ? "No templates match your filters"
                    : "No templates found"}
                </TableCell>
              </TableRow>
            ) : (
              paginatedTemplates.map((item) => {
                const { bg, color } = statusStyle(item.status);
                const type = getTemplateType(item);
                const rejectionReason = getRejectionReason(item);
                const isLongReason = rejectionReason.length > REJECTION_REASON_TRUNCATE_AT;
                return (
                  <TableRow key={item.template_id} sx={ROW_SX}>
                    <TableCell sx={cellSX}>{item.name || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{item.category || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{item.phone_number || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{type}</TableCell>
                    <TableCell sx={cellSX}>
                      <Badge label={item.status || "N/A"} bg={bg} color={color} />
                    </TableCell>
                    <TableCell sx={cellSX}>{item.language || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>
                      <Badge {...getHealthBadge(item.health_status)} dot />
                    </TableCell>
                    <TableCell sx={{ ...cellSX, maxWidth: 140 }}>
                      {!rejectionReason ? (
                        "-"
                      ) : isLongReason ? (
                        <Tooltip title={rejectionReason} arrow>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 120,
                              cursor: "help",
                              color: "#b91c1c",
                            }}
                          >
                            {rejectionReason}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: "0.72rem", color: "#b91c1c" }}>
                          {rejectionReason}
                        </Typography>
                      )}
                    </TableCell>
                    {/* ── Last Updated — nowrap keeps date & time on their own lines ── */}
                    <TableCell sx={{ ...cellSX, whiteSpace: "nowrap" }}>
                      {item.created_at ? (
                        <>
                          {new Date(item.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                          <br />
                          {new Date(item.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                        </>
                      ) : "N/A"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Tooltip title="Copy template">
                        <IconButton
                          size="small"
                          aria-label={`Copy template ${item.name || ""}`}
                          onClick={() => handleOpenCopy(item)}
                          sx={{ color: "primary.main", mr: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {["APPROVED", "REJECTED", "PAUSED"].includes(String(item.status || "").toUpperCase()) && (
                        <Tooltip title="Edit template">
                          <IconButton
                            size="small"
                            aria-label={`Edit template ${item.name || ""}`}
                            onClick={() => handleOpenEdit(item)}
                            sx={{ color: "primary.main", mr: 0.5 }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <PhoneAndroidIcon
                        sx={{ cursor: "pointer", color: "#1976d2", mr: 1 }}
                        onClick={() => { setSelectedTemplate(item); setPreviewOpen(true); }}
                      />
                      <DeleteIcon
                        sx={{ cursor: "pointer", color: "red", fontSize: "1.2rem" }}
                        onClick={() => handleDelete(item.name)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {!isLoading && !isError && filteredTemplates.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredTemplates.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": {
                fontSize: "0.75rem",
                minHeight: "48px",
              },
              "& .MuiTablePagination-selectLabel": {
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
              },
            }}
          />
        )}
      </TableContainer>

      {/* ═══════════════════════════════════════════════════════
          WHATSAPP PREVIEW DIALOG
      ═══════════════════════════════════════════════════════ */}
      <Dialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setSelectedTemplate(null); }}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": { bgcolor: "transparent", boxShadow: "none", p: 0, overflow: "visible" },
          "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.65)" },
        }}
      >
        <DialogContent sx={{
          p: 3, display: "flex", justifyContent: "center",
          alignItems: "flex-start", pt: 5,
          bgcolor: "transparent", overflow: "visible",
        }}>
          {/* Phone shell */}
          <Box sx={{
            width: 300,
            display: "flex", flexDirection: "column",
            bgcolor: "#000", borderRadius: "36px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
            overflow: "hidden", border: "6px solid #222",
          }}>
            {/* Status bar */}
            <Box sx={{ height: 28, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#075E54" }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#fff" }}>{dayjs().format("HH:mm")}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "#fff" }}>
                <WifiIcon sx={{ fontSize: 13 }} />
                <NetworkCellIcon sx={{ fontSize: 13 }} />
                <BatteryFullIcon sx={{ fontSize: 13 }} />
              </Stack>
            </Box>

            {/* WA app bar */}
            <Box sx={{ bgcolor: "#075E54", px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#128C7E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}>
                  {selectedTemplate?.name?.[0]?.toUpperCase() ?? "W"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedTemplate?.name ?? "Template"}
                </Typography>
                <Typography sx={{ fontSize: "0.59rem", color: "rgba(255,255,255,0.65)" }}>
                  {selectedTemplate?.category} • {selectedTemplate?.language}
                </Typography>
              </Box>
              {selectedTemplate?.status && (() => {
                const { bg, color } = statusStyle(selectedTemplate.status);
                return (
                  <Box sx={{ px: 0.9, py: 0.2, borderRadius: "8px", bgcolor: bg, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: "0.57rem", fontWeight: 700, color }}>{selectedTemplate.status}</Typography>
                  </Box>
                );
              })()}
            </Box>

            {/* Chat area */}
            <Box sx={{
              flexGrow: 1, overflowY: "auto",
              px: 1, pt: 1.5, pb: 2,
              minHeight: 360, maxHeight: "62vh",
              bgcolor: "#f5f5f5",
              "&::-webkit-scrollbar": { width: "3px" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#bbb", borderRadius: "2px" },
            }}>
              {selectedTemplate ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Date chip */}
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{
                      fontSize: "0.6rem", color: "#555",
                      bgcolor: "rgba(255,255,255,0.75)", px: 1.5, py: 0.3, borderRadius: "8px",
                      display: "inline-block",
                    }}>
                      {dayjs(selectedTemplate.created_at).isValid()
                        ? dayjs(selectedTemplate.created_at).format("MMM D, YYYY")
                        : "Today"}
                    </Typography>
                  </Box>

                  {/* Template content */}
                  <Box sx={{ width: "100%" }}>
                    {(() => {
                      const components = selectedTemplate?.components || [];
                      const carouselComp = components.find((c) => c.type === "CAROUSEL");
                      const buttonComp = components.find((c) => c.type === "BUTTONS");
                      const hasOTP = buttonComp?.buttons?.some((b) => b.type === "OTP");

                      // ── CAROUSEL ────────────────────────────────────────
                      if (carouselComp) {
                        const bodies = components.filter((c) => c.type === "BODY");
                        return (
                          <>
                            <CarouselPreviewGray cards={carouselComp.cards || []} />
                            {bodies.map((bodyComp, idx) => (
                              <Box key={idx} sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  {bodyComp.text}
                                </Typography>
                              </Box>
                            ))}
                          </>
                        );
                      }

                      // ── AUTHENTICATION TEMPLATE ────────────────────────
                      if (hasOTP) {
                        const bodyComp = components.find((c) => c.type === "BODY");
                        const footerComp = components.find((c) => c.type === "FOOTER");
                        const headerComp = components.find((c) => c.type === "HEADER");

                        return (
                          <>
                            {/* Security header for auth templates */}
                            <Box sx={{
                              bgcolor: "#fff",
                              borderRadius: "8px",
                              p: 1.5,
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              borderLeft: "4px solid #d32f2f"
                            }}>
                              <LockIcon sx={{ fontSize: 16, color: "#d32f2f", flexShrink: 0 }} />
                              <Box>
                                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#d32f2f" }}>
                                  Security verification
                                </Typography>
                                <Typography sx={{ fontSize: "0.65rem", color: "#666", mt: 0.3 }}>
                                  This is an authentication template
                                </Typography>
                              </Box>
                            </Box>

                            {/* Header text if present */}
                            {headerComp?.text && (
                              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#000" }}>
                                  {headerComp.text}
                                </Typography>
                              </Box>
                            )}

                            {/* Body - show default auth message if no text */}
                            <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                              {bodyComp?.text ? (
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  {bodyComp.text}
                                </Typography>
                              ) : bodyComp?.add_security_recommendation ? (
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  Your verification code is: <strong>@x!yz</strong>
                                  <br />
                                  <Typography sx={{ fontSize: "0.7rem", color: "#666", mt: 0.5 }}>
                                    Don't share this code with anyone. We'll never ask you for it.
                                  </Typography>
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  Your verification code is: <strong>@x!yz</strong>
                                </Typography>
                              )}
                            </Box>

                            {/* Footer with expiration if present */}
                            {footerComp?.code_expiration_minutes && (
                              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>
                                  Code expires in {footerComp.code_expiration_minutes} minute{footerComp.code_expiration_minutes > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}

                            {/* OTP Buttons */}
                            {buttonComp?.buttons?.length > 0 && (
                              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Stack spacing={1}>
                                  {buttonComp.buttons.map((btn, bIdx) => (
                                    <Tooltip
                                      key={bIdx}
                                      title={getButtonTooltip(btn)}
                                      arrow
                                      disableHoverListener={!shouldShowButtonTooltip(btn)}
                                    >
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                          fontSize: "0.7rem",
                                          textTransform: "none",
                                          borderRadius: "6px",
                                          bgcolor: "#075E54",
                                          color: "#fff",
                                          py: 1,
                                          "&:hover": { bgcolor: "#128C7E" }
                                        }}
                                      >
                                        <LockIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                        {getOTPButtonLabel(btn.otp_type)}
                                      </Button>
                                    </Tooltip>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </>
                        );
                      }

                      // ── STANDARD (TEXT / MEDIA) ─────────────────────────
                      return (
                        <>
                          {/* Header: IMAGE or VIDEO */}
                          {components.some((c) => c.type === "HEADER") && (
                            <Box sx={{ bgcolor: "#fff", borderRadius: "8px", overflow: "hidden", mb: 1 }}>
                              {components
                                .filter((c) => c.type === "HEADER")
                                .map((headerComp, idx) => {
                                  const mediaUrl = headerComp?.example?.header_handle?.[0] ?? null;
                                  const mediaFormat = headerComp.format ?? "IMAGE"; // IMAGE | VIDEO | TEXT | DOCUMENT

                                  return (
                                    <Box key={idx}>
                                      {/* IMAGE */}
                                      {mediaFormat === "IMAGE" && (
                                        <HeaderMedia url={mediaUrl} format="IMAGE" height={110} />
                                      )}

                                      {/* VIDEO */}
                                      {mediaFormat === "VIDEO" && (
                                        <HeaderMedia url={mediaUrl} format="VIDEO" height={140} />
                                      )}

                                      {/* DOCUMENT */}
                                      {mediaFormat === "DOCUMENT" && (
                                        <Box sx={{
                                          display: "flex", alignItems: "center", gap: 1,
                                          p: 1.5, bgcolor: "#fafafa",
                                        }}>
                                          <Box sx={{
                                            width: 36, height: 36, borderRadius: "6px",
                                            bgcolor: "#e53935", display: "flex",
                                            alignItems: "center", justifyContent: "center", flexShrink: 0,
                                          }}>
                                            <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#fff" }}>PDF</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: "0.72rem", color: "#333" }}>
                                            {mediaUrl ? "Document attached" : "No document"}
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* TEXT header */}
                                      {headerComp.text && mediaFormat !== "IMAGE" && mediaFormat !== "VIDEO" && mediaFormat !== "DOCUMENT" && (
                                        <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#000", p: 1 }}>
                                          {headerComp.text}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                            </Box>
                          )}

                          {/* Body & Footer */}
                          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                            {components.filter((c) => c.type === "BODY").map((bodyComp, idx) => (
                              <Typography key={idx} sx={{ fontSize: "0.8rem", color: "#333" }}>
                                {bodyComp.text}
                              </Typography>
                            ))}
                            {components.filter((c) => c.type === "FOOTER").map((footerComp, idx) => (
                              <Typography key={idx} sx={{ fontSize: "0.7rem", color: "#999", mt: 0.5 }}>
                                {footerComp.text}
                              </Typography>
                            ))}
                          </Box>

                          {/* Buttons */}
                          {components.some((c) => c.type === "BUTTONS") && (
                            <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem", mb: 0.5, display: "block" }}>
                                Suggested Actions
                              </Typography>
                              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                                {components
                                  .filter((c) => c.type === "BUTTONS")
                                  .flatMap((b) => b.buttons)
                                  .map((btn, bIdx) => (
                                    <Tooltip
                                      key={bIdx}
                                      title={getButtonTooltip(btn)}
                                      arrow
                                      disableHoverListener={!shouldShowButtonTooltip(btn)}
                                    >
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          fontSize: "0.65rem",
                                          textTransform: "none",
                                          borderRadius: "6px",
                                          ...(bIdx === 0
                                            ? { bgcolor: "#000", color: "#fff" }
                                            : { bgcolor: "#f0f0f0", color: "#333" }),
                                        }}
                                      >
                                        {btn.text}
                                      </Button>
                                    </Tooltip>
                                  ))}
                              </Stack>
                            </Box>
                          )}
                        </>
                      );
                    })()}

                    {/* Timestamp */}
                    <Typography sx={{ fontSize: "0.57rem", color: "#888", textAlign: "right", mt: 0.4 }}>
                      {dayjs(selectedTemplate.created_at).isValid()
                        ? dayjs(selectedTemplate.created_at).format("HH:mm")
                        : dayjs().format("HH:mm")} ✓✓
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
                  <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>No template selected</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Outside close button */}
          <IconButton
            onClick={() => { setPreviewOpen(false); setSelectedTemplate(null); }}
            sx={{ position: "absolute", top: 8, right: 8, color: "#fff", bgcolor: "rgba(0,0,0,0.45)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <EditTemplateDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        template={editingTemplate}
        formData={editFormData}
        setFormData={setEditFormData}
        onSubmit={handleEditSubmit}
        isSubmitting={isEditing}
      />

      {/* ── Copy Dialog ── */}
      <EditTemplateDialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        template={copyingTemplate}
        formData={copyFormData}
        setFormData={setCopyFormData}
        onSubmit={handleCopySubmit}
        isSubmitting={isCreating}
        isCopy
        fullyEdit={copyFullyEdit}
        onFullyEditChange={setCopyFullyEdit}
      />
    </Box>
  );
}

export default WhatsappList;